// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import Collection from "@kaspernj/api-maker/build/collection.js"
import Country from "./country.js"
import Translation from "./translation.js"
import User from "./user.js"

const modelClassData = {
  "attributes": {
    "created_at": {
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      },
      "name": "created_at",
      "selected_by_default": null,
      "translated": true
    },
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      },
      "name": "id",
      "selected_by_default": null,
      "translated": true
    },
    "locale": {
      "column": {
        "default": null,
        "name": "locale",
        "null": false,
        "type": "string"
      },
      "name": "locale",
      "selected_by_default": null,
      "translated": true
    },
    "updated_at": {
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      },
      "name": "updated_at",
      "selected_by_default": null,
      "translated": true
    },
    "name": {
      "column": null,
      "name": "name",
      "selected_by_default": false,
      "translated": true
    },
    "name_da": {
      "column": null,
      "name": "name_da",
      "selected_by_default": false,
      "translated": false
    },
    "name_en": {
      "column": null,
      "name": "name_en",
      "selected_by_default": false,
      "translated": false
    },
    "name_no": {
      "column": null,
      "name": "name_no",
      "selected_by_default": false,
      "translated": false
    },
    "name_se": {
      "column": null,
      "name": "name_se",
      "selected_by_default": false,
      "translated": false
    }
  },
  "className": "Locale",
  "collectionKey": "locales",
  "collectionName": "locales",
  "i18nKey": "locale",
  "camelizedLower": "locale",
  "name": "Locale",
  "nameDasherized": "locale",
  "pluralName": "locales",
  "ransackable_associations": [
    {
      "className": "Locale::Audit",
      "collectionName": "locale_audits",
      "foreignKey": "locale_id",
      "name": "audits",
      "macro": "has_many",
      "resource_name": "LocaleAudit",
      "through": null
    },
    {
      "className": "Locale::Audit",
      "collectionName": "locale_audits",
      "foreignKey": "locale_id",
      "name": "create_audit",
      "macro": "has_one",
      "resource_name": "LocaleAudit",
      "through": null
    },
    {
      "className": "Locale::Translation",
      "collectionName": null,
      "foreignKey": "locale_id",
      "name": "current_translation",
      "macro": "has_one",
      "resource_name": null,
      "through": null
    },
    {
      "className": "Country",
      "collectionName": "countries",
      "foreignKey": "default_locale_id",
      "name": "default_locale_on_countries",
      "macro": "has_many",
      "resource_name": "Country",
      "through": null
    },
    {
      "className": "::Translation",
      "collectionName": "translations",
      "foreignKey": "locale",
      "name": "translation_models",
      "macro": "has_many",
      "resource_name": "Translation",
      "through": null
    },
    {
      "className": "Locale::Translation",
      "collectionName": null,
      "foreignKey": "locale_id",
      "name": "translations",
      "macro": "has_many",
      "resource_name": null,
      "through": null
    },
    {
      "className": "User",
      "collectionName": "users",
      "foreignKey": "locale",
      "name": "users",
      "macro": "has_many",
      "resource_name": "User",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "locale",
      "column": {
        "default": null,
        "name": "locale",
        "null": false,
        "type": "string"
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    }
  ],
  "ransackable_scopes": [],
  "relationships": [
    {
      "className": "Country",
      "collectionName": "countries",
      "foreignKey": "default_locale_id",
      "name": "default_locale_on_countries",
      "macro": "has_many",
      "resource_name": "Country",
      "through": null
    },
    {
      "className": "::Translation",
      "collectionName": "translations",
      "foreignKey": "locale",
      "name": "translation_models",
      "macro": "has_many",
      "resource_name": "Translation",
      "through": null
    },
    {
      "className": "User",
      "collectionName": "users",
      "foreignKey": "locale",
      "name": "users",
      "macro": "has_many",
      "resource_name": "User",
      "through": null
    }
  ],
  "paramKey": "locale",
  "primaryKey": "id"
}

/** Frontend model for Locale. */
export default class Locale extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {Date} */
  createdAt() {
    return this.readAttributeUnderscore("created_at")
  }

  /** @returns {boolean} */
  hasCreatedAt() {
    const value = this.createdAt()

    return this._isPresent(value)
  }

  /** @returns {any} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {string} */
  locale() {
    return this.readAttributeUnderscore("locale")
  }

  /** @returns {boolean} */
  hasLocale() {
    const value = this.locale()

    return this._isPresent(value)
  }

  /** @returns {Date} */
  updatedAt() {
    return this.readAttributeUnderscore("updated_at")
  }

  /** @returns {boolean} */
  hasUpdatedAt() {
    const value = this.updatedAt()

    return this._isPresent(value)
  }

  /** @returns {string | null} */
  name() {
    return this.readAttributeUnderscore("name")
  }

  /** @returns {boolean} */
  hasName() {
    const value = this.name()

    return this._isPresent(value)
  }

  /** @returns {any} */
  nameDa() {
    return this.readAttributeUnderscore("name_da")
  }

  /** @returns {boolean} */
  hasNameDa() {
    const value = this.nameDa()

    return this._isPresent(value)
  }

  /** @returns {any} */
  nameEn() {
    return this.readAttributeUnderscore("name_en")
  }

  /** @returns {boolean} */
  hasNameEn() {
    const value = this.nameEn()

    return this._isPresent(value)
  }

  /** @returns {any} */
  nameNo() {
    return this.readAttributeUnderscore("name_no")
  }

  /** @returns {boolean} */
  hasNameNo() {
    const value = this.nameNo()

    return this._isPresent(value)
  }

  /** @returns {any} */
  nameSe() {
    return this.readAttributeUnderscore("name_se")
  }

  /** @returns {boolean} */
  hasNameSe() {
    const value = this.nameSe()

    return this._isPresent(value)
  }

  /** @returns {import("@kaspernj/api-maker/build/collection.js").default<typeof import("./country.js").default>} */
  defaultLocaleOnCountries() {
    if (!("id" in this)) throw new Error("No such primary key method: id")

    const ransack = {}

    ransack["default_locale_id_eq"] = this.id()

    return new Collection(
      {
        reflectionName: "default_locale_on_countries",
        model: this,
        modelClass: Country
      },
      {ransack}
    )
  }

  /** @returns {Promise<Array<import("./country.js").default>>} */
  loadDefaultLocaleOnCountries() {
    if (!("id" in this)) throw new Error("No such primary key method: id")

    const ransack = {}

    ransack["default_locale_id_eq"] = this.id()

    return this._loadHasManyReflection(
      {
        reflectionName: "default_locale_on_countries",
        model: this,
        modelClass: Country
      },
      {ransack}
    )
  }

  /** @returns {import("@kaspernj/api-maker/build/collection.js").default<typeof import("./translation.js").default>} */
  translationModels() {
    if (!("locale" in this)) throw new Error("No such primary key method: locale")

    const ransack = {}

    ransack["locale_eq"] = this.locale()

    return new Collection(
      {
        reflectionName: "translation_models",
        model: this,
        modelClass: Translation
      },
      {ransack}
    )
  }

  /** @returns {Promise<Array<import("./translation.js").default>>} */
  loadTranslationModels() {
    if (!("locale" in this)) throw new Error("No such primary key method: locale")

    const ransack = {}

    ransack["locale_eq"] = this.locale()

    return this._loadHasManyReflection(
      {
        reflectionName: "translation_models",
        model: this,
        modelClass: Translation
      },
      {ransack}
    )
  }

  /** @returns {import("@kaspernj/api-maker/build/collection.js").default<typeof import("./user.js").default>} */
  users() {
    if (!("locale" in this)) throw new Error("No such primary key method: locale")

    const ransack = {}

    ransack["locale_eq"] = this.locale()

    return new Collection(
      {
        reflectionName: "users",
        model: this,
        modelClass: User
      },
      {ransack}
    )
  }

  /** @returns {Promise<Array<import("./user.js").default>>} */
  loadUsers() {
    if (!("locale" in this)) throw new Error("No such primary key method: locale")

    const ransack = {}

    ransack["locale_eq"] = this.locale()

    return this._loadHasManyReflection(
      {
        reflectionName: "users",
        model: this,
        modelClass: User
      },
      {ransack}
    )
  }
}
